import { useEffect, useMemo, useState } from "react";
import { FileWithPath, useDropzone } from "react-dropzone";
import { FileDetails } from "@/types/types";
import { UploadFileToS3 } from "@/utils/uploadFilestoS3";
import PhotoPlus from "@/assets/icons/authentication/PhotoPlusOrange.svg";
import { Delete, Trash2 } from "lucide-react";

interface FileUploadProps {
  onFilesUploaded?: (files: FileDetails[]) => void;

  /** Profile photo: single file only */
  defaultValue?: FileDetails[];
  maxSize?: number;
  accept?: { [key: string]: string[] };

  clearFiles?: boolean;
  showSelectedFiles?: boolean;

  /** External state support (Redux-friendly) */
  useExternalState?: boolean;
  externalFiles?: FileDetails[];
  setExternalFiles?: React.Dispatch<React.SetStateAction<FileDetails[]>>;

  /** if you want to show live_url */
  useLiveUrl?: boolean;

  setVideosAreUpload?: React.Dispatch<React.SetStateAction<string | null>>;
}

const FileUploadProfileDesign: React.FC<FileUploadProps> = ({
  onFilesUploaded,
  useLiveUrl = false,
  showSelectedFiles = true,
  defaultValue = [],
  useExternalState = false,
  externalFiles = [],
  setExternalFiles,
  setVideosAreUpload,
  maxSize = 2 * 1024 * 1024, // ✅ 2MB default for profile photo
  accept = {
    "image/jpeg": [],
    "image/png": [],
    "image/jpg": [],
  },
  clearFiles = false,
}) => {
  const [localFiles, setLocalFiles] = useState<FileDetails[]>(defaultValue);
  const [showProgress, setShowProgress] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const files = useExternalState ? externalFiles : localFiles;
  const setFiles = useExternalState ? setExternalFiles : setLocalFiles;

  const acceptedText = useMemo(() => {
    const exts = Object.keys(accept)
      .map((t) => t.split("/")[1])
      .filter(Boolean)
      .map((x) => `.${String(x).toLowerCase()}`);
    return Array.from(new Set(exts)).join(" ");
  }, [accept]);

  const maxSizeText = useMemo(() => {
    const mb = maxSize / (1024 * 1024);
    return mb >= 1 ? `${Math.round(mb)}MB` : `${Math.round(maxSize / 1024)}KB`;
  }, [maxSize]);

  useEffect(() => {
    if (clearFiles && setFiles) {
      setFiles([]);
    }
  }, [clearFiles, setFiles]);

  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (uploading || !setFiles) return;

    // ✅ only 1
    const file = acceptedFiles?.[0];
    if (!file) return;

    setUploading(true);

    // ✅ replace any existing
    const uniqueNumber = new Date().getTime();
    const sanitizeFileName = (name: string) =>
      name.replace(/[^a-zA-Z0-9.\-_]/g, "_").slice(0, 100);

    const fileDetails: FileDetails = {
      name: `${uniqueNumber.toString()}-${sanitizeFileName(file.name)}`,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      webkitRelativePath: file.webkitRelativePath,
      local_url: URL.createObjectURL(file),
      live_url: null,
    };

    // set immediately (so UI shows preview)
    setFiles([fileDetails]);

    try {
      setShowProgress(fileDetails.name);
      setVideosAreUpload?.(fileDetails.name);

      const uploadedFile = await UploadFileToS3(fileDetails);

      const updatedFile: FileDetails = {
        ...fileDetails,
        live_url: uploadedFile.live_url,
      };

      setFiles([updatedFile]);
      onFilesUploaded?.([updatedFile]);
    } catch (error) {
      console.error(`Failed to upload file: ${file.name}`, error);
    } finally {
      setVideosAreUpload?.(null);
      setShowProgress(null);
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    if (!setFiles) return;
    setFiles([]);
    onFilesUploaded?.([]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false, // ✅ single upload
    maxSize,
    accept,
  });

  const hasPhoto = files.length > 0;

  return (
    <div className="mt-3">
      {/* Upload box: show ONLY if no photo */}
      {!hasPhoto && (
        <div
          {...getRootProps()}
          className="bg-[#FFFAF6] p-2 flex items-center gap-x-[9px] w-fit cursor-pointer select-none"
        >
          <input {...getInputProps()} />

          <div className="size-[60px] rounded-full border border-[#F4F4F4] flex items-center justify-center bg-white">
            <PhotoPlus />
          </div>

          <div>
            <h6 className="text-[#121217] text-xs tracking-[-1%] font-medium">
              Upload profile photo
            </h6>
            <p className="text-[#475467] text-[7px] tracking-[-1.7%] mt-1">
              Allowed {acceptedText || "images"}, Max size {maxSizeText || "—"}
            </p>
          </div>
        </div>
      )}

      {/* Selected photo */}
      {showSelectedFiles && hasPhoto && (
        <div className="mt-3">
          <div
            className="relative bg-[#F8F8F8] border border-[#EAECF0] rounded-[8px] overflow-hidden flex justify-center items-center"
            style={{ width: "124px", height: "124px" }}
          >
            <img
              src={
                !files[0].live_url
                  ? files[0].local_url
                  : useLiveUrl
                    ? (files[0].live_url as string)
                    : (files[0].local_url as string)
              }
              alt={files[0].name}
              className="object-cover w-full h-full"
            />

            {showProgress === files[0].name && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center text-[10px] font-medium text-[#475467]">
                Uploading...
              </div>
            )}

            {/* ✅ Delete always visible */}
            <button
              type="button"
              className="absolute top-2 right-2 bg-white p-1 rounded-md"
              onClick={handleRemoveFile}
              aria-label="Remove photo"
            >
              <Trash2 size={20} color="red"/>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadProfileDesign;
